import { useState, useCallback } from 'react';
import { decryptWithPassword, encryptWithPassword } from '@safermark/shared/crypto';
import type { VaultData, BookmarkItem, NoteItem } from '@safermark/shared/types';
import { apiClient } from '@safermark/shared/api';
import { useAuth } from './useAuth';

export interface UnlockedVault {
  data: VaultData;
  password: string;
  isCloudVault?: boolean;
}

export function useVaults() {
  const { token, isPremium, refreshPremiumStatus } = useAuth();
  const [unlockedVaults, setUnlockedVaults] = useState<Record<string, UnlockedVault>>({});
  const [cloudVaultMetas, setCloudVaultMetas] = useState<Array<{ id: string; lastModified: number; size: number }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Note: No longer using VaultManager for web app - handling cloud operations directly

  // Helper function to handle errors that might be due to premium status mismatch
  const handlePremiumMismatchError = useCallback(async (error: any, operation: string): Promise<void> => {
    // Check if error indicates premium access issue
    const errorMessage = error?.message || '';
    const isPremiumRelatedError = 
      errorMessage.includes('premium') ||
      errorMessage.includes('subscription') ||
      errorMessage.includes('upgrade') ||
      (error?.status === 403 && errorMessage.includes('access'));

    if (isPremiumRelatedError) {
      console.log(`${operation}: Detected potential premium status mismatch error, refreshing user data`);
      try {
        const freshUser = await refreshPremiumStatus();
        const freshIsPremium = freshUser?.isPremium || false;
        console.log(`${operation}: After refresh - Premium status: ${freshIsPremium ? 'Premium' : 'Free'}`);
        
        if (!freshIsPremium) {
          // Status is still non-premium after refresh, so the error is legitimate
          console.log(`${operation}: Confirmed user is not premium after refresh`);
        } else {
          // Status was updated to premium, suggest user retry
          console.log(`${operation}: Premium status updated after refresh - user should retry operation`);
        }
      } catch (refreshError) {
        console.warn(`${operation}: Failed to refresh premium status after error:`, refreshError);
      }
    }
  }, [refreshPremiumStatus]);

  // Helper function to sync vault data to cloud
  const syncVaultToCloud = useCallback(async (vaultId: string, vaultData: VaultData, password: string) => {
    if (!token) return;
    
    try {
      apiClient.setToken(token);
      const encrypted = await encryptWithPassword(password, vaultData);
      await apiClient.syncVault(vaultId, {
        vaultId,
        encrypted,
        lastModified: Date.now()
      });
    } catch (error) {
      console.error('Failed to sync vault to cloud:', error);
      // Handle potential premium status mismatch for sync operations
      await handlePremiumMismatchError(error, 'syncVaultToCloud');
      throw error;
    }
  }, [token, handlePremiumMismatchError]);

  // Load cloud vault metadata (for display)
  const loadCloudVaultMetas = useCallback(async () => {
    if (!token) {
      setCloudVaultMetas([]);
      return;
    }

    try {
      setLoading(true);
      apiClient.setToken(token);
      const response = await apiClient.getCloudVaults();
      setCloudVaultMetas(response.vaults);
      setError(null);
    } catch (err) {
      console.error('Error loading cloud vault metas:', err);
      setError('Failed to load cloud vaults');
      setCloudVaultMetas([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Unlock a cloud vault
  const unlockVault = useCallback(async (password: string) => {
    try {
      setLoading(true);
      setError(null);

      if (!token) {
        throw new Error('Authentication required');
      }

      if (!isPremium) {
        throw new Error('Premium subscription required to unlock cloud vaults in the web app. Please use the Chrome extension to access your vaults.');
      }

      // Get the vault ID for this password
      const { deriveCloudVaultId } = await import('@safermark/shared/crypto');
      const vaultId = await deriveCloudVaultId(password);

      // Fetch the specific vault from API
      apiClient.setToken(token);
      const { vault } = await apiClient.getCloudVault(vaultId);

      if (!vault) {
        throw new Error('No cloud vault matched this password.');
      }

      // Decrypt the vault data directly
      const vaultData = await decryptWithPassword<VaultData>(password, vault.encrypted);
      const migratedData = { ...vaultData, isCloudVault: true };

      // Add to unlocked vaults
      setUnlockedVaults(prev => ({
        ...prev,
        [migratedData.id]: {
          data: migratedData,
          password,
          isCloudVault: true
        }
      }));

      return migratedData;
    } catch (err) {
      // Handle potential premium status mismatch
      await handlePremiumMismatchError(err, 'unlockVault');
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to unlock vault';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token, isPremium, handlePremiumMismatchError]);

  // Lock a vault
  const lockVault = useCallback((vaultId: string) => {
    setUnlockedVaults(prev => {
      const updated = { ...prev };
      delete updated[vaultId];
      return updated;
    });
  }, []);

  // Create a new cloud vault
  const createVault = useCallback(async (password: string, name: string) => {
    try {
      setLoading(true);
      setError(null);

      if (!token) {
        throw new Error('Authentication required');
      }

      if (!isPremium) {
        throw new Error('Premium subscription required to create cloud vaults in the web app. Please use the Chrome extension to create vaults.');
      }

      // Import required functions
      const { deriveCloudVaultId, encryptWithPassword } = await import('@safermark/shared/crypto');

      // Create vault data directly
      const vaultId = await deriveCloudVaultId(password);
      const now = Date.now();
      const vaultData: VaultData = { 
        id: vaultId, 
        name, 
        bookmarks: [], 
        notes: [], 
        isCloudVault: true, 
        createdAt: now,
        updatedAt: now 
      };

      // Encrypt the vault data
      const encrypted = await encryptWithPassword(password, vaultData);

      // Sync to server
      apiClient.setToken(token);
      await apiClient.syncVault(vaultId, {
        vaultId,
        encrypted,
        lastModified: now
      });
      
      // Reload cloud vault metas
      await loadCloudVaultMetas();

      // Add to unlocked vaults
      setUnlockedVaults(prev => ({
        ...prev,
        [vaultData.id]: {
          data: vaultData,
          password,
          isCloudVault: true
        }
      }));

      return vaultData;
    } catch (err) {
      // Handle potential premium status mismatch
      await handlePremiumMismatchError(err, 'createVault');
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to create vault';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token, isPremium, loadCloudVaultMetas, handlePremiumMismatchError]);

  // Delete a cloud vault
  const deleteVault = useCallback(async (vaultId: string) => {
    try {
      setLoading(true);
      setError(null);

      if (!token) {
        throw new Error('Authentication required');
      }

      // Delete from server
      apiClient.setToken(token);
      await apiClient.deleteCloudVault(vaultId);
      await loadCloudVaultMetas();

      // Remove from unlocked vaults if it exists there
      const unlockedVault = unlockedVaults[vaultId];
      if (unlockedVault) {
        lockVault(vaultId);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete vault';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [unlockedVaults, token, loadCloudVaultMetas, lockVault]);

  // Add bookmark to vault
  const addBookmark = useCallback(async (vaultId: string, bookmark: Omit<BookmarkItem, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const unlockedVault = unlockedVaults[vaultId];
      if (!unlockedVault) {
        throw new Error('Vault must be unlocked to add bookmark');
      }

      if (!isPremium) {
        throw new Error('Premium subscription required to modify vault contents in the web app.');
      }

      // Web app handles vault operations directly without VaultManager

      const newBookmark: BookmarkItem = {
        ...bookmark,
        id: crypto.randomUUID(),
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      // For web app, manually update vault data instead of using VaultManager methods
      const updatedVaultData = {
        ...unlockedVault.data,
        bookmarks: [newBookmark, ...unlockedVault.data.bookmarks],
        updatedAt: Date.now()
      };

      setUnlockedVaults(prev => ({
        ...prev,
        [vaultId]: {
          ...prev[vaultId],
          data: updatedVaultData
        }
      }));

      // Sync to cloud
      await syncVaultToCloud(vaultId, updatedVaultData, unlockedVault.password);

      return newBookmark;
    } catch (err) {
      // Handle potential premium status mismatch
      await handlePremiumMismatchError(err, 'addBookmark');
      throw err;
    }
  }, [unlockedVaults, isPremium, token, syncVaultToCloud, handlePremiumMismatchError]);

  // Add note to vault
  const addNote = useCallback(async (vaultId: string, note: Omit<NoteItem, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const unlockedVault = unlockedVaults[vaultId];
      if (!unlockedVault) {
        throw new Error('Vault must be unlocked to add note');
      }

      if (!isPremium) {
        throw new Error('Premium subscription required to modify vault contents in the web app.');
      }

      // Web app handles vault operations directly without VaultManager

      const newNote: NoteItem = {
        ...note,
        id: crypto.randomUUID(),
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      // For web app, manually update vault data instead of using VaultManager methods
      const updatedVaultData = {
        ...unlockedVault.data,
        notes: [newNote, ...unlockedVault.data.notes],
        updatedAt: Date.now()
      };

      setUnlockedVaults(prev => ({
        ...prev,
        [vaultId]: {
          ...prev[vaultId],
          data: updatedVaultData
        }
      }));

      // Sync to cloud
      await syncVaultToCloud(vaultId, updatedVaultData, unlockedVault.password);

      return newNote;
    } catch (err) {
      // Handle potential premium status mismatch
      await handlePremiumMismatchError(err, 'addNote');
      throw err;
    }
  }, [unlockedVaults, isPremium, token, syncVaultToCloud, handlePremiumMismatchError]);

  // Update item in vault
  const updateItem = useCallback(async (vaultId: string, itemId: string, updatedItem: BookmarkItem | NoteItem) => {
    try {
      const unlockedVault = unlockedVaults[vaultId];
      if (!unlockedVault) {
        throw new Error('Vault must be unlocked to update item');
      }

      if (!isPremium) {
        throw new Error('Premium subscription required to modify vault contents in the web app.');
      }

      // Web app handles vault operations directly without VaultManager

      // For web app, manually update vault data instead of using VaultManager methods
      const updatedVaultData = { ...unlockedVault.data };
      
      if (updatedItem.type === 'bookmark') {
        const index = updatedVaultData.bookmarks.findIndex(b => b.id === itemId);
        if (index >= 0) {
          updatedVaultData.bookmarks[index] = { ...updatedItem, updatedAt: Date.now() } as BookmarkItem;
        } else {
          throw new Error('Bookmark not found');
        }
      } else {
        const index = updatedVaultData.notes.findIndex(n => n.id === itemId);
        if (index >= 0) {
          updatedVaultData.notes[index] = { ...updatedItem, updatedAt: Date.now() } as NoteItem;
        } else {
          throw new Error('Note not found');
        }
      }
      
      updatedVaultData.updatedAt = Date.now();

      setUnlockedVaults(prev => ({
        ...prev,
        [vaultId]: {
          ...prev[vaultId],
          data: updatedVaultData
        }
      }));

      // Sync to cloud
      await syncVaultToCloud(vaultId, updatedVaultData, unlockedVault.password);
    } catch (err) {
      // Handle potential premium status mismatch
      await handlePremiumMismatchError(err, 'updateItem');
      throw err;
    }
  }, [unlockedVaults, isPremium, token, syncVaultToCloud, handlePremiumMismatchError]);

  // Delete item from vault
  const deleteItem = useCallback(async (vaultId: string, itemId: string) => {
    try {
      const unlockedVault = unlockedVaults[vaultId];
      if (!unlockedVault) {
        throw new Error('Vault must be unlocked to delete item');
      }

      if (!isPremium) {
        throw new Error('Premium subscription required to modify vault contents in the web app.');
      }

      // Web app handles vault operations directly without VaultManager

      // For web app, manually update vault data instead of using VaultManager methods
      const updatedVaultData = { ...unlockedVault.data };
      
      // Try to remove from bookmarks first
      const bookmarkIndex = updatedVaultData.bookmarks.findIndex(b => b.id === itemId);
      if (bookmarkIndex >= 0) {
        updatedVaultData.bookmarks.splice(bookmarkIndex, 1);
      } else {
        // Try to remove from notes
        const noteIndex = updatedVaultData.notes.findIndex(n => n.id === itemId);
        if (noteIndex >= 0) {
          updatedVaultData.notes.splice(noteIndex, 1);
        } else {
          throw new Error('Item not found');
        }
      }
      
      updatedVaultData.updatedAt = Date.now();

      setUnlockedVaults(prev => ({
        ...prev,
        [vaultId]: {
          ...prev[vaultId],
          data: updatedVaultData
        }
      }));

      // Sync to cloud
      await syncVaultToCloud(vaultId, updatedVaultData, unlockedVault.password);
    } catch (err) {
      // Handle potential premium status mismatch
      await handlePremiumMismatchError(err, 'deleteItem');
      throw err;
    }
  }, [unlockedVaults, isPremium, token, syncVaultToCloud, handlePremiumMismatchError]);

  return {
    // State
    unlockedVaults,
    cloudVaultMetas,
    loading,
    error,
    
    // Actions
    loadCloudVaultMetas,
    unlockVault,
    lockVault,
    createVault,
    deleteVault,
    addBookmark,
    addNote,
    updateItem,
    deleteItem,
    
    // Utils
    clearError: () => setError(null)
  };
}